import { useState, useEffect, useCallback, useRef } from 'react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from 'recharts'
import { supabase } from '../../../shared/lib/supabase'
import { useAuth } from '../../../contexts/AuthContext'
import { useOrg } from '../../../contexts/OrgContext'
import { useAIJobs } from '../../../contexts/AIJobsContext'
import { logAuditEvent } from '../../../shared/lib/auditLog'
import { extractTextFromStoragePath } from '../../../shared/utils/pdfTextExtract'
import { exportInsuranceReviewDocx } from '../../../shared/utils/insuranceReviewDocxExport'
import { resolveCarrierGroup } from '../../../shared/utils/resolveCarrierGroup'

// Cyber Insurance Policies section. Advisor-only (RLS enforces).
// Uploads land in existing client-documents bucket under category='cyber_insurance'.
// Phase B: AI review pipeline live. Both single and portfolio (stacked coverage) modes.

const MAX_FILE_SIZE = 25 * 1024 * 1024
const ACCEPT_TYPES = ['application/pdf']

function formatFileSize(bytes) {
  if (!bytes) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

function formatCurrency(n) {
  if (n == null) return '—'
  return '$' + Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })
}

function CyberInsurancePoliciesSection({ client }) {
  const { user, profile } = useAuth()
  const { currentOrg } = useOrg()
  const { startJob, getJob } = useAIJobs()
  const clientId = client?.id

  const [policies, setPolicies] = useState([])
  const [singleReviews, setSingleReviews] = useState({})   // { [document_id]: latestReview }
  const [portfolioReview, setPortfolioReview] = useState(null) // latest portfolio review for this client
  const [orgBrandConfig, setOrgBrandConfig] = useState(null)
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState('')
  const [policyLabel, setPolicyLabel] = useState('')
  const [selectedFiles, setSelectedFiles] = useState([])
  const [error, setError] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [editingLabelId, setEditingLabelId] = useState(null)
  const [editingLabelValue, setEditingLabelValue] = useState('')
  const [expandedReviewId, setExpandedReviewId] = useState(null)
  const [expandPortfolio, setExpandPortfolio] = useState(true)
  const [exportingId, setExportingId] = useState(null)  // 'portfolio' or document_id of single review being exported
  const fileInputRef = useRef(null)
  // Tracks job completedAt timestamps we've already acted on, so useEffect
  // watchers don't refetch in a loop on every re-render.
  const processedJobsRef = useRef(new Set())

  const portfolioJobKey = clientId ? `insurance-review:${clientId}:portfolio` : null
  const portfolioJob = portfolioJobKey ? getJob(portfolioJobKey) : null
  const portfolioRunning = portfolioJob?.status === 'running'

  const fetchPolicies = useCallback(async () => {
    if (!clientId) return
    setLoading(true)
    try {
      const { data: docs, error: docErr } = await supabase
        .from('client_documents')
        .select('*')
        .eq('client_id', clientId)
        .eq('category', 'cyber_insurance')
        .order('created_at', { ascending: false })
      if (docErr) throw docErr
      setPolicies(docs || [])

      const { data: reviewRows } = await supabase
        .from('client_insurance_policy_reviews')
        .select('*')
        .eq('client_id', clientId)
        .order('reviewed_at', { ascending: false })

      const singleMap = {}
      let portfolio = null
      ;(reviewRows || []).forEach(r => {
        if (r.review_type === 'single' && r.document_id && !singleMap[r.document_id]) {
          singleMap[r.document_id] = r
        } else if (r.review_type === 'portfolio' && !portfolio) {
          portfolio = r
        }
      })
      setSingleReviews(singleMap)
      setPortfolioReview(portfolio)
    } catch (err) {
      console.error('[CyberInsurance] Fetch policies failed:', err)
      setError('Unable to load policy list')
    } finally {
      setLoading(false)
    }
  }, [clientId])

  useEffect(() => { fetchPolicies() }, [fetchPolicies])

  // Fetch org brand config for DOCX export branding
  useEffect(() => {
    if (!currentOrg?.id) return
    supabase.from('org_brand_config').select('*').eq('org_id', currentOrg.id).maybeSingle()
      .then(({ data }) => { if (data) setOrgBrandConfig(data) })
  }, [currentOrg?.id])

  const buildBrand = useCallback(() => ({
    firmName: orgBrandConfig?.firm_display_name || currentOrg?.name || 'Sidewalk Security Advisors',
    logoUrl: orgBrandConfig?.logo_url || null,
    primaryHex: orgBrandConfig?.primary_color || '0D1B2A',
    accentHex: orgBrandConfig?.accent_color || 'C9A84C',
    trademarkLine: orgBrandConfig?.trademark_line || null,
    font: orgBrandConfig?.font_preference || 'Aptos'
  }), [orgBrandConfig, currentOrg])

  const handleExportReview = async (review, ctxId) => {
    if (!review || !client) return
    setExportingId(ctxId)
    setError('')
    try {
      await exportInsuranceReviewDocx({ client, review, brand: buildBrand() })
      logAuditEvent({
        category: 'change',
        action: `insurance_review.exported.${review.review_type || 'single'}`,
        description: `Exported ${review.review_type === 'portfolio' ? 'portfolio' : 'single'} insurance review for ${client.name}`,
        clientId,
        clientName: client?.name,
        resourceType: 'insurance_review',
        metadata: { reviewId: review.id, type: review.review_type, reviewedAt: review.reviewed_at }
      })
    } catch (err) {
      console.error('[Insurance Review] Export failed:', err)
      setError('Export failed: ' + (err?.message || 'unknown error'))
    } finally {
      setExportingId(null)
    }
  }

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || [])
    setSelectedFiles(files)
    setError('')
  }

  const handleUpload = async () => {
    if (selectedFiles.length === 0 || !clientId) return
    setError('')
    for (const f of selectedFiles) {
      if (!ACCEPT_TYPES.includes(f.type)) return setError(`${f.name}: only PDF policies are supported`)
      if (f.size > MAX_FILE_SIZE) return setError(`${f.name}: file size must be under 25MB`)
    }

    const batchMode = selectedFiles.length > 1
    const labelForSingle = policyLabel.trim() || null

    setUploading(true)
    const failed = []
    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const f = selectedFiles[i]
        setUploadProgress(`Uploading ${i + 1} of ${selectedFiles.length}: ${f.name}`)
        try {
          const fileId = crypto.randomUUID()
          const filePath = `${clientId}/${fileId}-${f.name}`
          const { error: uploadErr } = await supabase.storage
            .from('client-documents')
            .upload(filePath, f, { contentType: f.type, upsert: false })
          if (uploadErr) throw uploadErr
          const { error: dbErr } = await supabase
            .from('client_documents')
            .insert({
              client_id: clientId,
              file_name: f.name,
              file_path: filePath,
              file_size: f.size,
              mime_type: f.type,
              category: 'cyber_insurance',
              description: batchMode ? null : labelForSingle,
              uploaded_by: user.id,
              uploaded_by_role: profile?.role === 'client' ? 'client' : 'admin'
            })
          if (dbErr) throw dbErr
          logAuditEvent({
            category: 'change', action: 'insurance_policy.uploaded',
            description: `Uploaded cyber insurance policy: ${f.name}${!batchMode && labelForSingle ? ` (${labelForSingle})` : ''}`,
            clientId, clientName: client?.name, resourceType: 'document',
            metadata: { fileName: f.name, fileSize: f.size, policyLabel: batchMode ? null : labelForSingle, batch: batchMode }
          })
        } catch (fileErr) {
          failed.push({ name: f.name, reason: fileErr.message })
        }
      }
      if (failed.length > 0) setError(`Uploaded ${selectedFiles.length - failed.length} of ${selectedFiles.length}. Failed: ${failed.map(f => `${f.name} (${f.reason})`).join(', ')}`)
      setSelectedFiles([])
      setPolicyLabel('')
      if (fileInputRef.current) fileInputRef.current.value = ''
      await fetchPolicies()
    } catch (err) {
      setError(err.message || 'Upload failed')
    } finally {
      setUploading(false)
      setUploadProgress('')
    }
  }

  const handleSaveLabel = async (doc) => {
    const newLabel = editingLabelValue.trim() || null
    if (newLabel === (doc.description || null)) {
      setEditingLabelId(null); setEditingLabelValue(''); return
    }
    try {
      const { error: updateErr } = await supabase
        .from('client_documents').update({ description: newLabel }).eq('id', doc.id)
      if (updateErr) throw updateErr
      logAuditEvent({
        category: 'change', action: 'insurance_policy.renamed',
        description: `Renamed cyber insurance policy to "${newLabel || doc.file_name}"`,
        clientId, clientName: client?.name, resourceType: 'document',
        metadata: { policyLabel: newLabel, fileName: doc.file_name }
      })
      setEditingLabelId(null); setEditingLabelValue('')
      await fetchPolicies()
    } catch (err) {
      setError('Failed to save label: ' + err.message)
    }
  }

  const startEditingLabel = (doc) => {
    setEditingLabelId(doc.id); setEditingLabelValue(doc.description || '')
  }

  const handleDownload = async (doc) => {
    try {
      const { data, error: dlErr } = await supabase.storage
        .from('client-documents').createSignedUrl(doc.file_path, 60)
      if (dlErr) throw dlErr
      window.open(data.signedUrl, '_blank')
    } catch (err) {
      setError('Download failed: ' + err.message)
    }
  }

  const handleDelete = async () => {
    if (!deleteConfirm) return
    setDeleting(true)
    try {
      await supabase.storage.from('client-documents').remove([deleteConfirm.file_path])
      await supabase.from('client_documents').delete().eq('id', deleteConfirm.id)
      // Mark any surviving review rows as pdf_status=deleted (document_id already null via FK)
      await supabase
        .from('client_insurance_policy_reviews')
        .update({ pdf_status: 'deleted' })
        .eq('client_id', clientId)
        .is('document_id', null)
      logAuditEvent({
        category: 'change', action: 'insurance_policy.deleted',
        description: `Deleted cyber insurance policy: ${deleteConfirm.file_name}`,
        clientId, clientName: client?.name, resourceType: 'document'
      })
      setDeleteConfirm(null)
      await fetchPolicies()
    } catch (err) {
      setError('Delete failed: ' + err.message)
    } finally {
      setDeleting(false)
    }
  }

  // ─── Review flows ───
  //
  // Design: kick off the job and return. Do NOT await its result in the
  // handler. Post-completion side effects (refetch, auto-expand, error
  // display) live in useEffect watchers so they fire even if the advisor
  // navigated away and came back. AIJobsContext keeps the in-flight promise
  // alive via inFlightRef so the underlying Anthropic call continues
  // regardless of component unmount.

  const runReview = ({ mode, docs, jobKey, label }) => {
    setError('')

    startJob(jobKey, async () => {
      const { data: { session } } = await supabase.auth.getSession()

      // 1. Extract text from each PDF (client-side, via pdf.js)
      const extracted = []
      for (let i = 0; i < docs.length; i++) {
        const d = docs[i]
        try {
          const { text } = await extractTextFromStoragePath(supabase, 'client-documents', d.file_path)
          if (!text || text.trim().length < 100) {
            throw new Error(`${d.file_name}: extracted text is empty or very short. PDF may be image-based (needs OCR).`)
          }
          extracted.push({
            documentId: d.id,
            fileName: d.file_name,
            label: d.description || null,
            extractedText: text
          })
        } catch (err) {
          throw new Error(`PDF extraction failed for ${d.file_name}: ${err.message}`)
        }
      }

      // 2. Call the review endpoint
      const res = await fetch('/api/ai/insurance-review', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {})
        },
        body: JSON.stringify({
          mode,
          clientId,
          policyLabel: label || null,
          documents: extracted
        })
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.error || `Review failed (${res.status})`)
      }
      const result = await res.json()
      result._meta = { mode, docCount: docs.length, firstFileName: docs[0]?.file_name }
      return result
    }).catch(err => {
      // Surface to the watcher effect through job state; nothing to do here
      console.error(`[Insurance Review] ${mode} failed:`, err)
    })
  }

  const handleRunSingle = (doc) => {
    if (!clientId) return
    runReview({
      mode: 'single',
      docs: [doc],
      jobKey: `insurance-review:${clientId}:single:${doc.id}`,
      label: doc.description || null
    })
  }

  const handleRunPortfolio = () => {
    if (policies.length < 2 || !clientId) return
    runReview({ mode: 'portfolio', docs: policies, jobKey: portfolioJobKey, label: null })
  }

  const getSingleJobFor = (docId) => getJob(`insurance-review:${clientId}:single:${docId}`)

  // Watch the portfolio job; on completion (success or error) refetch +
  // auto-expand (on success) or surface error (on error). Dedup via
  // processedJobsRef so we only act once per completedAt timestamp.
  useEffect(() => {
    if (!portfolioJob?.completedAt) return
    const stamp = `portfolio:${portfolioJob.completedAt}`
    if (processedJobsRef.current.has(stamp)) return
    processedJobsRef.current.add(stamp)

    if (portfolioJob.status === 'success') {
      const result = portfolioJob.result
      logAuditEvent({
        category: 'change',
        action: 'insurance_review.ran.portfolio',
        description: `Ran portfolio insurance review for ${result?._meta?.docCount || '?'} policies`,
        clientId, clientName: client?.name, resourceType: 'insurance_review',
        metadata: { mode: 'portfolio', documentCount: result?._meta?.docCount, residualRisks: result?.residualRisks?.length || 0 }
      })
      setExpandPortfolio(true)
      fetchPolicies()
    } else if (portfolioJob.status === 'error') {
      setError(portfolioJob.error || 'Portfolio review failed')
    }
  }, [portfolioJob?.status, portfolioJob?.completedAt, portfolioJob?.error, clientId, client?.name, fetchPolicies])

  // Watch each policy's single-review job. Runs per render but cheap; dedup
  // on completedAt stamps.
  useEffect(() => {
    for (const p of policies) {
      const job = getSingleJobFor(p.id)
      if (!job?.completedAt) continue
      const stamp = `single:${p.id}:${job.completedAt}`
      if (processedJobsRef.current.has(stamp)) continue
      processedJobsRef.current.add(stamp)

      if (job.status === 'success') {
        const result = job.result
        logAuditEvent({
          category: 'change',
          action: 'insurance_review.ran.single',
          description: `Ran single insurance review for ${p.file_name}`,
          clientId, clientName: client?.name, resourceType: 'insurance_review',
          metadata: { mode: 'single', fileName: p.file_name, residualRisks: result?.residualRisks?.length || 0 }
        })
        setExpandedReviewId(p.id)
        fetchPolicies()
      } else if (job.status === 'error') {
        setError(`Review of ${p.file_name} failed: ${job.error || 'unknown error'}`)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policies.map(p => `${p.id}:${getSingleJobFor(p.id)?.completedAt || ''}`).join('|'), clientId, client?.name])

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-700/30 pb-3 mb-4 gap-4">
        <div className="min-w-0">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Cyber Insurance Policy Review</h3>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            Upload policy PDFs for AI-assisted gap analysis. Stacked coverage supported — add multiple policies.
          </p>
        </div>
        {policies.length >= 2 && (
          <button
            type="button"
            onClick={handleRunPortfolio}
            disabled={portfolioRunning}
            title="Analyze all uploaded policies together as a stacked program"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed rounded-lg transition-colors shadow-sm whitespace-nowrap flex-shrink-0"
          >
            {portfolioRunning ? (
              <>
                <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Running Portfolio Review...
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                </svg>
                Run Portfolio Review
              </>
            )}
          </button>
        )}
      </div>

      <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-xs text-blue-800 dark:text-blue-300">
        <p className="font-semibold mb-1">How this works</p>
        <p>
          Policy PDFs are encrypted at rest and never sent to third parties. During review, text is extracted in your browser and pseudonymized on our server before any AI call. Your insured name, policy numbers, broker details, and personal contacts are stripped. Findings are saved as a non-sensitive summary that survives PDF deletion.
        </p>
      </div>

      {/* Portfolio review card — appears above the policy list when one exists */}
      {portfolioReview && (
        <PortfolioReviewCard
          review={portfolioReview}
          expanded={expandPortfolio}
          onToggle={() => setExpandPortfolio(v => !v)}
          policies={policies}
          onExport={() => handleExportReview(portfolioReview, 'portfolio')}
          exporting={exportingId === 'portfolio'}
        />
      )}

      {/* Upload form */}
      <div className="space-y-3 mb-5">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
          <input
            type="text"
            value={policyLabel}
            onChange={(e) => setPolicyLabel(e.target.value)}
            placeholder={selectedFiles.length > 1
              ? 'Label ignored for multi-file upload — rename each policy inline below'
              : 'Policy label (e.g. Primary Layer - Coalition $1M)'}
            disabled={selectedFiles.length > 1}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-60 disabled:cursor-not-allowed"
          />
          <div className="flex gap-2">
            <input
              ref={fileInputRef} type="file" accept="application/pdf" multiple onChange={handleFileChange}
              className="block w-full text-sm text-gray-700 dark:text-gray-300 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-gray-100 dark:file:bg-gray-700 file:text-gray-700 dark:file:text-gray-200 hover:file:bg-gray-200 dark:hover:file:bg-gray-600"
            />
            <button
              type="button" onClick={handleUpload} disabled={uploading || selectedFiles.length === 0}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed rounded-lg transition-colors shadow-sm whitespace-nowrap"
            >
              {uploading ? (uploadProgress || 'Uploading...') : (selectedFiles.length > 1 ? `Upload ${selectedFiles.length} Policies` : 'Upload Policy')}
            </button>
          </div>
        </div>
        <p className="text-[11px] text-gray-500 dark:text-gray-400">
          PDF only, 25MB per file. Select multiple files for stacked coverage. Uploads also appear in the Document Vault under Cyber Insurance.
        </p>
        {error && (
          <div className="text-xs text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">{error}</div>
        )}
      </div>

      {/* Policy list */}
      {loading ? (
        <div className="text-center py-6 text-sm text-gray-500 dark:text-gray-400">Loading policies...</div>
      ) : policies.length === 0 ? (
        <div className="text-center py-8 border border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
          <svg className="w-10 h-10 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-sm text-gray-500 dark:text-gray-400">No policies uploaded yet</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Upload a cyber insurance policy PDF to begin</p>
        </div>
      ) : (
        <div className="space-y-2">
          {policies.map(p => {
            const latestReview = singleReviews[p.id]
            const singleJob = getSingleJobFor(p.id)
            const singleRunning = singleJob?.status === 'running'
            const isExpanded = expandedReviewId === p.id
            return (
              <div key={p.id} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
                <div className="flex items-center justify-between gap-3 p-3">
                  <div className="min-w-0 flex-grow">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm3 2h6v2H7V6zm0 4h6v2H7v-2z" clipRule="evenodd" />
                      </svg>
                      {editingLabelId === p.id ? (
                        <div className="flex items-center gap-2 flex-grow">
                          <input
                            type="text" autoFocus value={editingLabelValue}
                            onChange={(e) => setEditingLabelValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveLabel(p)
                              if (e.key === 'Escape') { setEditingLabelId(null); setEditingLabelValue('') }
                            }}
                            placeholder={p.file_name}
                            className="flex-grow text-sm px-2 py-1 border border-primary-400 dark:border-primary-500 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                          />
                          <button type="button" onClick={() => handleSaveLabel(p)} className="text-xs font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-700">Save</button>
                          <button type="button" onClick={() => { setEditingLabelId(null); setEditingLabelValue('') }} className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700">Cancel</button>
                        </div>
                      ) : (
                        <>
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{p.description || p.file_name}</p>
                          <button type="button" onClick={() => startEditingLabel(p)} className="p-1 rounded text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors flex-shrink-0" title="Edit label">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                      <span>{p.file_name}</span>
                      <span>•</span>
                      <span>{formatFileSize(p.file_size)}</span>
                      <span>•</span>
                      <span>Uploaded {new Date(p.created_at).toLocaleDateString()}</span>
                      {latestReview && (
                        <>
                          <span>•</span>
                          <span className="text-green-600 dark:text-green-400 font-medium">Reviewed {new Date(latestReview.reviewed_at).toLocaleDateString()}</span>
                          <button type="button" onClick={() => setExpandedReviewId(isExpanded ? null : p.id)} className="text-primary-600 dark:text-primary-400 hover:underline">
                            {isExpanded ? 'Hide' : 'View'} findings
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button type="button" onClick={() => handleDownload(p)} className="p-1.5 rounded-md text-gray-500 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors" title="Download PDF">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRunSingle(p)}
                      disabled={singleRunning || portfolioRunning}
                      title={singleRunning ? 'Review in progress' : latestReview ? 'Re-run review with current client context' : 'Run AI review'}
                      className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed rounded-md transition-colors"
                    >
                      {singleRunning ? (
                        <>
                          <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                          Running...
                        </>
                      ) : (
                        <>
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          {latestReview ? 'Re-run' : 'Run Review'}
                        </>
                      )}
                    </button>
                    {latestReview && (
                      <button
                        type="button"
                        onClick={() => handleExportReview(latestReview, p.id)}
                        disabled={exportingId === p.id}
                        title="Export Insurance Review Summary as .docx"
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60 disabled:cursor-not-allowed rounded-md transition-colors"
                      >
                        {exportingId === p.id ? (
                          <>
                            <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            Exporting...
                          </>
                        ) : (
                          <>
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Export
                          </>
                        )}
                      </button>
                    )}
                    <button type="button" onClick={() => setDeleteConfirm(p)} className="p-1.5 rounded-md text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors" title="Delete policy PDF">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
                {isExpanded && latestReview && (
                  <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-3">
                    <SingleReviewFindings review={latestReview} />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setDeleteConfirm(null)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-sm mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">Delete Policy</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">This removes the PDF. Any saved review findings are kept.</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
              Permanently remove <span className="font-medium">{deleteConfirm.description || deleteConfirm.file_name}</span>?
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteConfirm(null)} disabled={deleting} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">Cancel</button>
              <button onClick={handleDelete} disabled={deleting} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50">
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Sub-components: findings display ───

// Severity indicator — small dot + label, no chunky pill. Modeled after
// Linear/Vercel/Stripe — gives status without screaming.
const SEVERITY_TONES = {
  critical: { dot: 'bg-red-600',    text: 'text-red-700 dark:text-red-400',    label: 'Critical' },
  high:     { dot: 'bg-red-500',    text: 'text-red-700 dark:text-red-400',    label: 'High' },
  medium:   { dot: 'bg-amber-500',  text: 'text-amber-700 dark:text-amber-400',label: 'Medium' },
  low:      { dot: 'bg-gray-400',   text: 'text-gray-600 dark:text-gray-400',  label: 'Low' }
}

function SeverityBadge({ severity }) {
  const t = SEVERITY_TONES[String(severity || '').toLowerCase()] || SEVERITY_TONES.low
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium">
      <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} aria-hidden />
      <span className={t.text}>{t.label}</span>
    </span>
  )
}

// Confidence — text-only with semantic color, no background pill.
function ConfidencePill({ score }) {
  if (score == null) return null
  const tone = score >= 80 ? 'text-green-700 dark:text-green-400'
    : score >= 60 ? 'text-blue-700 dark:text-blue-400'
    : 'text-amber-700 dark:text-amber-400'
  return (
    <span className={`text-xs font-medium ${tone}`} title="QA confidence">
      QA confidence {score}%
    </span>
  )
}

function SingleReviewFindings({ review }) {
  const f = review.findings || {}
  const summary = f.policySummary || {}
  const confidence = review.review_confidence?.overallConfidence
  return (
    <div className="space-y-3 text-sm">
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
          {summary.carrier && <span><strong className="text-gray-900 dark:text-white">{summary.carrier}</strong></span>}
          {summary.coverageLimitPerEvent != null && <span>• {formatCurrency(summary.coverageLimitPerEvent)}/event</span>}
          {summary.retention != null && <span>• {formatCurrency(summary.retention)} retention</span>}
          {summary.effectiveDate && <span>• Eff {summary.effectiveDate}</span>}
          {summary.expirationDate && <span>— Exp {summary.expirationDate}</span>}
        </div>
        <ConfidencePill score={confidence} />
      </div>
      {review.pdf_status === 'deleted' && (
        <div className="text-[11px] italic text-gray-500 dark:text-gray-400">Original PDF has been deleted. Findings below persist as non-sensitive summary.</div>
      )}
      {Array.isArray(f.strengths) && f.strengths.length > 0 && (
        <div>
          <h5 className="text-xs font-bold uppercase tracking-wider text-green-700 dark:text-green-400 mb-1">Strengths</h5>
          <ul className="space-y-1">
            {f.strengths.map((s, i) => (
              <li key={i} className="text-xs text-gray-700 dark:text-gray-300">
                <span className="font-semibold">{s.title}:</span> {s.detail}
              </li>
            ))}
          </ul>
        </div>
      )}
      {Array.isArray(f.gaps) && f.gaps.length > 0 && (
        <div>
          <h5 className="text-xs font-bold uppercase tracking-wider text-red-700 dark:text-red-400 mb-1">Gaps</h5>
          <SeverityGroupedList items={f.gaps} severityKey="severity" itemType="gap" />
        </div>
      )}
      {Array.isArray(f.recommendations) && f.recommendations.length > 0 && (
        <div>
          <h5 className="text-xs font-bold uppercase tracking-wider text-primary-700 dark:text-primary-400 mb-1">Recommendations</h5>
          <SeverityGroupedList items={f.recommendations} severityKey="priority" itemType="recommendation" />
        </div>
      )}
      {(review.review_confidence?.flags || []).length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-amber-700 dark:text-amber-400 font-medium">
            {review.review_confidence.flags.length} QA flag{review.review_confidence.flags.length === 1 ? '' : 's'} — click to view
          </summary>
          <ul className="mt-1 space-y-1 pl-4">
            {review.review_confidence.flags.map((flag, i) => (
              <li key={i} className="text-gray-600 dark:text-gray-400">
                <SeverityBadge severity={flag.severity} /> <span className="font-medium">{flag.field}:</span> {flag.issue} <span className="italic">({flag.suggestion})</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  )
}

function PortfolioReviewCard({ review, expanded, onToggle, policies, onExport, exporting }) {
  const f = review.findings || {}
  const summary = f.portfolioSummary || {}
  const cross = f.crossLayerFindings || {}
  const confidence = review.review_confidence?.overallConfidence
  const layerCount = summary.layerCount || (review.document_ids?.length || policies.length)
  const currentDocIds = new Set(policies.map(p => p.id))
  const reviewedDocIds = Array.isArray(review.document_ids) ? review.document_ids : []
  const driftFromPolicies = reviewedDocIds.some(id => !currentDocIds.has(id)) ||
    policies.some(p => !reviewedDocIds.includes(p.id))

  return (
    <div className="mb-4 rounded-xl border-2 border-primary-200 dark:border-primary-800 bg-primary-50/30 dark:bg-primary-900/20">
      <div className="flex items-center justify-between gap-3 p-4 cursor-pointer" onClick={onToggle}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-primary-600 text-white flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
            </svg>
          </div>
          <div>
            <h4 className="text-sm font-bold text-primary-800 dark:text-primary-300">Portfolio Review — {layerCount} layer{layerCount === 1 ? '' : 's'}</h4>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {summary.totalAggregateCoverage != null && <>Total aggregate {formatCurrency(summary.totalAggregateCoverage)} • </>}
              Reviewed {new Date(review.reviewed_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onExport && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onExport() }}
              disabled={exporting}
              title="Export Portfolio Review Summary as .docx"
              className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-primary-700 dark:text-primary-300 bg-white dark:bg-gray-800 border border-primary-300 dark:border-primary-700 hover:bg-primary-50 dark:hover:bg-primary-900/40 disabled:opacity-60 disabled:cursor-not-allowed rounded-md transition-colors"
            >
              {exporting ? (
                <>
                  <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Exporting...
                </>
              ) : (
                <>
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export .docx
                </>
              )}
            </button>
          )}
          <ConfidencePill score={confidence} />
          {driftFromPolicies && (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-amber-700 dark:text-amber-400">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" aria-hidden />
              Policy set changed — re-run suggested
            </span>
          )}
          <svg className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
      {expanded && (
        <div className="border-t border-primary-200 dark:border-primary-800 px-4 py-4 space-y-5 text-sm">

          {/* Coverage stack chart + layer table side-by-side on wide screens */}
          {Array.isArray(summary.layers) && summary.layers.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-4">
              <CoverageStackChart layers={summary.layers} totalAggregate={summary.totalAggregateCoverage} />
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-1.5 font-semibold text-gray-600 dark:text-gray-400">Layer</th>
                      <th className="text-left py-1.5 font-semibold text-gray-600 dark:text-gray-400">Carrier</th>
                      <th className="text-right py-1.5 font-semibold text-gray-600 dark:text-gray-400">Limit</th>
                      <th className="text-right py-1.5 font-semibold text-gray-600 dark:text-gray-400">Attachment</th>
                      <th className="text-center py-1.5 font-semibold text-gray-600 dark:text-gray-400">Follow-Form</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.layers.map((L, i) => {
                      const group = resolveCarrierGroup(L.carrier)
                      return (
                      <tr key={i} className="border-b border-gray-100 dark:border-gray-800">
                        <td className="py-1.5">{L.order || i + 1}</td>
                        <td className="py-1.5">
                          {L.carrier ? (
                            <span>
                              <span>{L.carrier}</span>
                              {group?.brand && (
                                <span className="block text-[10px] text-gray-500 dark:text-gray-400" title={group.context || ''}>
                                  {group.brand}
                                </span>
                              )}
                            </span>
                          ) : '—'}
                        </td>
                        <td className="py-1.5 text-right">{formatCurrency(L.limit)}</td>
                        <td className="py-1.5 text-right">{formatCurrency(L.attachmentPoint)}</td>
                        <td className="py-1.5 text-center">
                          {L.isFollowForm == null
                            ? <span className="text-gray-400">—</span>
                            : L.isFollowForm
                              ? <span className="text-green-600 dark:text-green-400 font-semibold">Yes</span>
                              : <span className="text-amber-600 dark:text-amber-400 font-semibold">No</span>}
                        </td>
                      </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Cross-layer scorecard — replaces the long dl */}
          {Object.keys(cross).length > 0 && (
            <div>
              <h5 className="text-xs font-bold uppercase tracking-wider text-primary-700 dark:text-primary-400 mb-2">Cross-Layer Analysis</h5>
              <CrossLayerScorecard cross={cross} />
            </div>
          )}

          {/* Severity-grouped Gaps */}
          {Array.isArray(f.programGaps) && f.programGaps.length > 0 && (
            <div>
              <h5 className="text-xs font-bold uppercase tracking-wider text-red-700 dark:text-red-400 mb-2">Program Gaps</h5>
              <SeverityGroupedList items={f.programGaps} severityKey="severity" itemType="gap" />
            </div>
          )}

          {/* Severity-grouped Recommendations */}
          {Array.isArray(f.programRecommendations) && f.programRecommendations.length > 0 && (
            <div>
              <h5 className="text-xs font-bold uppercase tracking-wider text-primary-700 dark:text-primary-400 mb-2">Program Recommendations</h5>
              <SeverityGroupedList items={f.programRecommendations} severityKey="priority" itemType="recommendation" />
            </div>
          )}

          {(review.review_confidence?.flags || []).length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-amber-700 dark:text-amber-400 font-medium">
                {review.review_confidence.flags.length} QA flag{review.review_confidence.flags.length === 1 ? '' : 's'} — click to view
              </summary>
              <ul className="mt-1 space-y-1 pl-4">
                {review.review_confidence.flags.map((flag, i) => (
                  <li key={i} className="text-gray-600 dark:text-gray-400">
                    <SeverityBadge severity={flag.severity} /> <span className="font-medium">{flag.field}:</span> {flag.issue} <span className="italic">({flag.suggestion})</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Visualization helpers (Phase B.5) ───

// Heuristic status derivation from freeform text. Returns one of:
//   'aligned' (green) | 'asymmetric' (amber) | 'concern' (red) | 'unknown' (gray)
function deriveCrossLayerStatus(text) {
  if (!text || typeof text !== 'string') return 'unknown'
  const lower = text.toLowerCase()
  if (/\b(carve[\s-]?out|carved out|excludes?|exclude[ds]|conflict|gap|missing|delays?|disput|trigger.*gap|coverage gap)/.test(lower)) return 'concern'
  if (/\b(asymmetr|inconsistent|differ\b|differs|differing|mismatch|varies)/.test(lower)) return 'asymmetric'
  if (/\b(follow[s]?[\s-]?form|consistent|aligned|matching|harmoniz|uniform|stack|cumulat)/.test(lower)) return 'aligned'
  if (/\b(unclear|ambig|unspecified|not stated|not addressed|cannot determine|insufficient)/.test(lower)) return 'unknown'
  return 'review'
}

// Cross-Layer status — left border + dot indicator + sentence-case label
const CROSS_LAYER_TONES = {
  aligned:    { label: 'Aligned',    border: 'border-l-green-500',  dot: 'bg-green-500',  text: 'text-green-700 dark:text-green-400' },
  asymmetric: { label: 'Asymmetric', border: 'border-l-amber-500',  dot: 'bg-amber-500',  text: 'text-amber-700 dark:text-amber-400' },
  concern:    { label: 'Concern',    border: 'border-l-red-500',    dot: 'bg-red-500',    text: 'text-red-700 dark:text-red-400' },
  unknown:    { label: 'Unclear',    border: 'border-l-gray-300 dark:border-l-gray-600', dot: 'bg-gray-400', text: 'text-gray-500 dark:text-gray-400' },
  review:     { label: 'Review',     border: 'border-l-blue-500',   dot: 'bg-blue-500',   text: 'text-blue-700 dark:text-blue-400' }
}

function CrossLayerStatusLabel({ status }) {
  const t = CROSS_LAYER_TONES[status] || CROSS_LAYER_TONES.review
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium">
      <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} aria-hidden />
      <span className={t.text}>{t.label}</span>
    </span>
  )
}

// Small info icon with hover/focus tooltip describing what a Cross-Layer
// dimension means. Click is suppressed so the parent card's expand/collapse
// isn't triggered by tooltip interaction.
function DimensionInfoTooltip({ text }) {
  return (
    <span className="group relative inline-flex items-center flex-shrink-0">
      <button
        type="button"
        tabIndex={0}
        onClick={(e) => e.stopPropagation()}
        aria-label="What is this?"
        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded-full"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </button>
      <span
        role="tooltip"
        className="invisible opacity-0 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100 transition-opacity duration-150 pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-50 w-64 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-[11px] leading-relaxed rounded-md shadow-lg"
      >
        {text}
        <span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-x-4 border-x-transparent border-t-4 border-t-gray-900 dark:border-t-gray-700" />
      </span>
    </span>
  )
}

function CrossLayerCard({ title, description, text, items }) {
  const [open, setOpen] = useState(false)
  if (!text && !items) return null
  const status = items ? (items.some(i => i.severity === 'high' || i.severity === 'critical') ? 'concern' : 'asymmetric') : deriveCrossLayerStatus(text)
  const tone = CROSS_LAYER_TONES[status] || CROSS_LAYER_TONES.review
  const summary = text
    ? (text.length > 90 ? text.split(/(?<=\.)\s/)[0] : text)
    : items
      ? `${items.length} carve-out${items.length === 1 ? '' : 's'} identified`
      : ''
  return (
    <div className={`rounded-md border border-l-[3px] ${tone.border} ${open ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700' : 'bg-gray-50 dark:bg-gray-800/40 border-gray-200 dark:border-gray-700'} transition-colors`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left"
      >
        <div className="flex flex-col gap-0.5 min-w-0">
          <span className="flex items-center gap-1.5 min-w-0">
            <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">{title}</span>
            {description && <DimensionInfoTooltip text={description} />}
          </span>
          <CrossLayerStatusLabel status={status} />
        </div>
        <svg className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      <div className="px-3 pb-2.5 text-xs leading-relaxed text-gray-600 dark:text-gray-400">{summary}</div>
      {open && (
        <div className="border-t border-gray-100 dark:border-gray-700/50 px-3 py-2.5 text-xs leading-relaxed text-gray-700 dark:text-gray-300">
          {text && <p>{text}</p>}
          {Array.isArray(items) && items.length > 0 && (
            <ul className="space-y-2 mt-2">
              {items.map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="mt-1"><SeverityBadge severity={item.severity} /></span>
                  <span>{item.detail}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

function CrossLayerScorecard({ cross }) {
  const dimensions = [
    {
      key: 'followFormConsistency',
      title: 'Follow-Form Consistency',
      description: 'Whether excess layers adopt the primary policy\'s terms verbatim. Inconsistent forms can create unexpected coverage gaps when claims trigger excess attachment.'
    },
    {
      key: 'erosionAndDropDown',
      title: 'Erosion & Drop-Down',
      description: 'How excess layers respond when underlying coverage is exhausted. Drop-down clauses fill the gap if the primary fails or pays less than expected. Without them, exhaustion can leave claims uncovered mid-event.'
    },
    {
      key: 'sublimitCarryDown',
      title: 'Sublimit Carry-Down',
      description: 'Whether sublimits (e.g., a $250K Funds Transfer Fraud cap) reset at each layer or remain capped at the primary\'s lower amount across the entire program. Carry-down materially reduces stacked program effectiveness for sublimited coverages.'
    },
    {
      key: 'retentionStructure',
      title: 'Retention Structure',
      description: 'Whether the deductible/SIR is per-claim, aggregate, or applied separately per insuring agreement. Affects how losses count against retention and how quickly coverage attaches.'
    },
    {
      key: 'consentPanelConflicts',
      title: 'Consent & Panel Counsel',
      description: 'Whether different layers require different incident response vendors, breach counsel, or settlement consent terms. Conflicts cause real delays during active claims and can void coverage if the wrong panel is engaged.'
    }
  ]
  const exclusionAsymmetryDesc = 'When one layer excludes coverage another layer provides (e.g., primary covers ransomware, excess carves it out). Creates unexpected mid-claim gaps that aren\'t obvious from limit and retention numbers alone.'

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
      {dimensions.map(d => {
        const text = cross[d.key]
        if (!text) return null
        return <CrossLayerCard key={d.key} title={d.title} description={d.description} text={text} />
      })}
      {Array.isArray(cross.exclusionAsymmetry) && cross.exclusionAsymmetry.length > 0 && (
        <CrossLayerCard title="Exclusion Asymmetry" description={exclusionAsymmetryDesc} items={cross.exclusionAsymmetry} />
      )}
    </div>
  )
}

// Coverage Stack chart — renders layers as a stacked vertical bar so the
// program literally looks stacked. Each segment colored by layer order.
const LAYER_COLORS = ['#0d9488', '#0891b2', '#2563eb', '#7c3aed', '#9333ea', '#c026d3']

function CoverageStackChart({ layers, totalAggregate }) {
  // Build a single data point with one key per layer so Recharts stacks them.
  const point = { name: 'Program' }
  layers.forEach((L, i) => {
    point[`layer${i}`] = Number(L.limit) || 0
  })

  // Size the chart to fit the longest carrier name. ~6.5px per character at
  // 10pt + Y-axis label space (~70px) + bar padding (~24px). Clamp 260–520px
  // so the layout stays balanced on small and very long carrier names.
  const longestCarrier = layers.reduce((max, L) => Math.max(max, (L.carrier || '').length), 0)
  const chartWidth = Math.min(520, Math.max(260, longestCarrier * 6.5 + 100))

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3" style={{ width: chartWidth }}>
      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Coverage Stack</p>
      {totalAggregate != null && (
        <p className="text-base font-bold text-gray-900 dark:text-white">{formatCurrency(totalAggregate)}</p>
      )}
      <div style={{ width: '100%', height: 180 }}>
        <ResponsiveContainer>
          <BarChart data={[point]} margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
            <XAxis dataKey="name" tick={false} axisLine={false} />
            <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} tickFormatter={(v) => v >= 1000000 ? `$${v / 1000000}M` : v >= 1000 ? `$${v / 1000}K` : `$${v}`} width={50} />
            <Tooltip
              cursor={false}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null
                return (
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded shadow-md p-2 text-xs">
                    {payload.slice().reverse().map((entry, i) => {
                      const idx = parseInt(entry.dataKey.replace('layer', ''))
                      const L = layers[idx]
                      return (
                        <div key={i} className="flex items-center gap-2 py-0.5">
                          <span className="w-2 h-2 rounded-sm" style={{ background: entry.color }} />
                          <span className="text-gray-700 dark:text-gray-300">
                            <span className="font-semibold">{L?.carrier || `Layer ${idx + 1}`}</span> {formatCurrency(entry.value)}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                )
              }}
            />
            {layers.map((L, i) => (
              <Bar key={i} dataKey={`layer${i}`} stackId="program" fill={LAYER_COLORS[i % LAYER_COLORS.length]}>
                <LabelList
                  dataKey={`layer${i}`}
                  content={({ x, y, width, height, value }) => {
                    if (!value || height < 14 || width < 30) return null
                    // Fit the carrier name to the bar width. Roughly 6px per
                    // character at 10pt; pad 12px total. If the truncated
                    // version would be too short to be useful, fall back to
                    // the layer number.
                    const carrier = L.carrier || `Layer ${i + 1}`
                    const maxChars = Math.max(3, Math.floor((width - 12) / 6))
                    let display
                    if (carrier.length <= maxChars) {
                      display = carrier
                    } else if (maxChars < 8) {
                      display = `L${i + 1}`
                    } else {
                      display = carrier.slice(0, maxChars - 1).trimEnd() + '…'
                    }
                    return (
                      <text x={x + width / 2} y={y + height / 2 + 3} textAnchor="middle" fontSize="10" fill="white" fontWeight="600">
                        {display}
                      </text>
                    )
                  }}
                />
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

// Severity-grouped list — splits items into Critical / High / Medium / Low
// columns. Each column header is a discreet count + sentence-case label with
// dot indicator. Items use a subtle left border in the severity color.
function SeverityGroupedList({ items, severityKey, itemType }) {
  const severities = ['critical', 'high', 'medium', 'low']
  const grouped = severities.reduce((acc, s) => {
    acc[s] = items.filter(i => (i[severityKey] || 'medium').toLowerCase() === s)
    return acc
  }, {})

  const itemBorder = {
    critical: 'border-l-red-600',
    high:     'border-l-red-500',
    medium:   'border-l-amber-500',
    low:      'border-l-gray-300 dark:border-l-gray-600'
  }

  const visible = severities.filter(s => grouped[s].length > 0)
  if (visible.length === 0) return null

  // Equal-width columns scaled to whatever severities are actually present.
  // Each visible severity gets the same column width across the card.
  const colsClass = (
    visible.length === 1 ? 'grid-cols-1' :
    visible.length === 2 ? 'grid-cols-1 md:grid-cols-2' :
    visible.length === 3 ? 'grid-cols-1 md:grid-cols-3' :
    'grid-cols-1 md:grid-cols-2 xl:grid-cols-4'
  )

  return (
    <div className={`grid gap-3 w-full ${colsClass}`}>
      {visible.map(s => (
        <div key={s} className="space-y-2 min-w-0">
          <div className="flex items-center justify-between px-1">
            <SeverityBadge severity={s} />
            <span className="text-[11px] text-gray-400 dark:text-gray-500 tabular-nums">{grouped[s].length}</span>
          </div>
          <ul className="space-y-2">
            {grouped[s].map((item, i) => (
              <li key={i} className={`rounded-md border border-gray-200 dark:border-gray-700 border-l-[3px] ${itemBorder[s]} bg-white dark:bg-gray-800 p-3`}>
                <p className="text-xs font-semibold text-gray-900 dark:text-white leading-snug">{item.title}</p>
                <p className="text-[11px] text-gray-700 dark:text-gray-300 mt-1.5 leading-relaxed">{item.detail}</p>
                {item.businessImpact && (
                  <p className="text-[11px] text-gray-700 dark:text-gray-300 mt-2 leading-relaxed">
                    <span className="font-semibold text-red-700 dark:text-red-400">Business impact —</span> {item.businessImpact}
                  </p>
                )}
                {item.rationale && itemType === 'recommendation' && (
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 italic mt-2 leading-relaxed">{item.rationale}</p>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )
}

export default CyberInsurancePoliciesSection
